import {$qs} from '../../../../@shared/ts/dom';
import {cartCalculationAbortController, requestCartCalculation} from '../../payment/order';
import {startModalLoading, stopModalLoading} from '../../reducers/environmentReducer';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {store} from '../../store';
import {debounce} from '../../util/debounce';
import {gotoPage} from '../modal';
import {renderLongAddress} from './addressLocale';

/**
 * Setup event listeners for the shipping form.
 */
function initShippingFormEvents() {
	const $shippingFieldset = $qs<HTMLFieldSetElement>('#pp-shipping-fieldset');
	if (!$shippingFieldset) {
		throw new Error('Failed to locate the shipping fieldset element using the selector "#pp-shipping-fieldset"');
	}

	const $longAddress = $qs<HTMLSpanElement>('#long-address');
	if (!$longAddress) {
		throw new Error('Failed to locate the long address element using the selector "#long-address"');
	}

	/**
	 * Handle rendering the shipping fields depending on the "ship_to_different_address"
	 * checkbox checked state.
	 */
	$qs('#pp-shipping-form [name="ship_to_different_address"]')?.addEventListener('change', async e => {
		const $target = e.target as HTMLInputElement;
		if (!$target) {
			return;
		}

		if ($target.checked) {
			$shippingFieldset.classList.remove('hide');
			$shippingFieldset.disabled = false;

			$longAddress.classList.add('hide');
		} else {
			$shippingFieldset.classList.add('hide');
			$shippingFieldset.disabled = true;

			$longAddress.classList.remove('hide');
		}

		renderLongAddress();

		store.dispatch(startModalLoading());
		await requestCartCalculation();
		store.dispatch(stopModalLoading());
	});

	const $shippingForm = $qs<HTMLFormElement>('#pp-shipping-form');
	if (!$shippingForm) {
		throw new Error('Failed to locate the shipping form element using the selector "#pp-shipping-form"');
	}

	/**
	 * Handle recalculating the checkout whenever specific shipping fields are
	 * changed. Callback is debounced to reduce multiple requests.
	 */
	$shippingForm.addEventListener('change',
		debounce(async (e: Event) => {
			const $target = e.target as HTMLInputElement | null;
			if (!$target) {
				return;
			}

			const recalculateOn = [
				// For address recalculation
				'shipping_address_1',
				'shipping_address_2',
				'shipping_city',
				'shipping_postcode',
				'shipping_state',
				'shipping_country',
			];

			if (!recalculateOn.includes($target.getAttribute('name') ?? '')) {
				return;
			}

			store.dispatch(startModalLoading());
			await requestCartCalculation();
			store.dispatch(stopModalLoading());
		}, 1500, cartCalculationAbortController));

	/**
	 * Prevent the form from submitting when the enter key is pressed unless the
	 * element is a button or textarea.
	 */
	$shippingForm.addEventListener('keydown', e => {
		if (e.key !== 'Enter') {
			return;
		}

		const $target = e.target as HTMLInputElement;
		if ($target.tagName === 'BUTTON'
			|| $target.tagName === 'TEXTAREA'
			|| ($target.tagName === 'INPUT' && $target.type === 'submit')) {
			return;
		}

		e.preventDefault();
	});

	/**
	 * Handle validating the shipping fields when the form is submitted. Changes
	 * to the next page if there are no errors.
	 */
	$shippingForm.addEventListener('submit', async (event: Event) => {
		event.preventDefault();

		if (!await PeachPayOrder.shipping.reportValidity() || !await PeachPayOrder.additional.reportValidity()) {
			return;
		}

		store.dispatch(startModalLoading());
		if (!await gotoPage('payment')) {
			store.dispatch(stopModalLoading());
			return;
		}

		await requestCartCalculation();

		store.dispatch(stopModalLoading());
	});

	renderLongAddress();
}

export {
	initShippingFormEvents,
};
